import { useWatch, useFormContext } from 'react-hook-form';
import { useCreationTab } from '../creation-tab-context';
import { Column, Row } from 'app/design/primitives/box';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Switch } from 'app/design/primitives/switch';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';

import { useNavigation } from 'app/hooks/navigation';
import { isPublicServiceCategory } from 'app/network/category';
import type { CreateListingDTO, Listing } from 'app/types/listing';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TextInput, View } from 'react-native';
import { Pressable } from 'react-native-gesture-handler';
import { TimePickerModal } from 'react-native-paper-dates';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function PublicServiceRequestSection({
  numParticipants,
  listingId,
}: {
  numParticipants?: number | null;
  listingId?: Listing['id'];
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const creationTab = useCreationTab();

  const { control, setValue } = useFormContext<CreateListingDTO>(); // Workaround for https://github.com/react-hook-form/react-hook-form/issues/12916

  const serviceCategoryId = useWatch({ control, name: 'serviceCategoryId' });
  const isPublicServiceRequest = isPublicServiceCategory(serviceCategoryId);

  const [timePickerVisible, setTimePickerVisible] = useState<'timeStart' | 'timeEnd' | false>(false);

  const timeStart = useWatch({ control, name: 'timeStart' });
  const timeEnd = useWatch({ control, name: 'timeEnd' });
  const address = useWatch({ control, name: 'address' });

  const [timePickerHours, setTimePickerHours] = useState<number>();
  const [timePickerMinutes, setTimePickerMinutes] = useState<number>();

  const selectTime = (fieldName: typeof timePickerVisible) => {
    const time = (fieldName === 'timeStart' ? timeStart : timeEnd) ?? (fieldName === 'timeStart' ? '08:00' : '17:00');
    const [hours, minutes] = time.split(':');
    setTimePickerHours(Number(hours));
    setTimePickerMinutes(Number(minutes));
    setTimePickerVisible(fieldName);
  };

  const saveTime = ({ hours, minutes }: { hours: number; minutes: number }) => {
    if (!timePickerVisible) return;
    const pad = (num: number) => String(num).padStart(2, '0');
    setTimePickerVisible(false);
    const time = `${pad(hours)}:${pad(minutes)}`;
    setValue(timePickerVisible, time);
  };

  const chooseAddress = () => {
    navigation.navigate(
      'my-address',
      {
        address,
        goBackTo: numParticipants !== undefined ? 'edit-listing' : creationTab ? 'publish' : 'new',
        listingId,
      },
      { pop: true },
    );
  };

  const maxParticipantsInputRef = useRef<TextInput>(null);

  const editMaxParticipants = () => {
    maxParticipantsInputRef.current?.focus();
  };

  const timesSource = Boolean(timeStart || timeEnd);
  const [timesDraft, setTimesDraft] = useState(() => ({ source: timesSource, visible: timesSource }));
  let currentTimesDraft = timesDraft;
  if (timesDraft.source !== timesSource) {
    currentTimesDraft = { source: timesSource, visible: timesSource };
    setTimesDraft(currentTimesDraft);
  }
  const timesVisible = currentTimesDraft.visible;

  const setTimesVisible = (visible: boolean) => {
    setTimesDraft({ source: timesSource, visible });
  };

  const previousTimeStart = useRef<string | null | undefined>(null);
  const previousTimeEnd = useRef<string | null | undefined>(null);

  const toggleTimes = () => {
    // Clear timeStart & timeEnd in the form, when the toggle is switched off, but still remember the values when the toggle is switched on again
    if (timesVisible) {
      previousTimeStart.current = timeStart;
      previousTimeEnd.current = timeEnd;
      setValue('timeStart', null);
      setValue('timeEnd', null);
    } else {
      setValue('timeStart', previousTimeStart.current);
      setValue('timeEnd', previousTimeEnd.current);
    }
    setTimesVisible(!timesVisible);
  };

  if (!isPublicServiceRequest) {
    return null;
  }

  return (
    <>
      <Column marginTop="spacing.general.x4">
        <Row
          gap="spacing.general.x1"
          marginBottom="spacing.general.x2"
          justifyContent="space-between"
          alignItems="center"
        >
          <Row gap="spacing.general.x1">
            <Text color="text.primary" fontWeight="700" variant="bodyBase">
              {t('timeSection.title', { ns: 'new' })}
            </Text>
            <Text color="text.primary" variant="bodyBase">
              ({t('optionales', { ns: 'common' })})
            </Text>
          </Row>
          <Switch value={timesVisible} onValueChange={toggleTimes} />
        </Row>

        {timesVisible ? (
          <Column
            gap="spacing.general.x2"
            style={{
              borderColor: theme.colors['border.secondary'],
              borderWidth: 1,
              borderRadius: theme.borderRadii['radius.lg'],
              padding: theme.spacing['spacing.general.x2'],
              paddingLeft: theme.spacing['spacing.general.x4'],
            }}
          >
            <Pressable onPress={() => selectTime('timeStart')}>
              <Row alignItems="center" justifyContent="space-between">
                <Text variant="bodyBase">{t('timeSection.labelStart', { ns: 'new' })}</Text>
                <Text
                  padding="spacing.general.x4"
                  style={{
                    backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
                    borderColor: theme.colors['border.secondary'],
                    borderWidth: 1,
                    borderRadius: theme.borderRadii['radius.lg'],
                  }}
                  variant="bodyBase"
                >
                  {timeStart ?? '--:--'}
                </Text>
              </Row>
            </Pressable>

            <Separator color={theme.colors['surface.inverse.tertiary-neutral']} />

            <Pressable onPress={() => selectTime('timeEnd')}>
              <Row alignItems="center" justifyContent="space-between">
                <Text variant="bodyBase">{t('timeSection.labelEnd', { ns: 'new' })}</Text>
                <Text
                  padding="spacing.general.x4"
                  style={{
                    backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
                    borderColor: theme.colors['border.secondary'],
                    borderWidth: 1,
                    borderRadius: theme.borderRadii['radius.lg'],
                  }}
                  variant="bodyBase"
                >
                  {timeEnd ?? '--:--'}
                </Text>
              </Row>
            </Pressable>
          </Column>
        ) : null}
      </Column>

      <TimePickerModal
        cancelLabel={t('cancel', { ns: 'common' })}
        confirmLabel="OK"
        hours={timePickerHours}
        label={t(`timeSection.label${timePickerVisible === 'timeEnd' ? 'End' : 'Start'}`, { ns: 'new' })}
        minutes={timePickerMinutes}
        onDismiss={() => setTimePickerVisible(false)}
        onConfirm={saveTime}
        use24HourClock
        visible={!!timePickerVisible}
      />

      <Column marginTop="spacing.general.x6">
        <Row gap="spacing.general.x1" marginBottom="spacing.general.x2">
          <Text color="text.primary" fontWeight="700" variant="bodyBase">
            {t('addressSection.title', { ns: 'new' })}
          </Text>
          <Text color="text.primary" variant="bodyBase">
            ({t('optionale', { ns: 'common' })})
          </Text>
        </Row>

        <Column
          gap="spacing.general.x2"
          style={{
            borderColor: theme.colors['border.secondary'],
            borderWidth: 1,
            borderRadius: theme.borderRadii['radius.lg'],
            padding: theme.spacing['spacing.general.x2'],
            paddingLeft: theme.spacing['spacing.general.x4'],
          }}
        >
          <Pressable onPress={chooseAddress}>
            <Row alignItems="center" justifyContent="space-between">
              <Text variant="bodyBase" style={{ flexShrink: 1 }} numberOfLines={1}>
                {address
                  ? [
                      address.street,
                      address.unit ? `#${address.unit.replace('#', '')}` : null,
                      address.city,
                      address.administrativeArea,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : '-'}
              </Text>
              <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
            </Row>
          </Pressable>
        </Column>
      </Column>

      <View
        style={{
          backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
          borderRadius: theme.borderRadii['radius.xl'],
          paddingHorizontal: 20,
          paddingVertical: 25,
          marginTop: theme.spacing['spacing.general.x6'],
        }}
      >
        <Text fontWeight="700" textAlign="center" variant="bodyBase">
          {t('subscriptionsSection.title', { ns: 'one-listing' })}
        </Text>

        <Pressable
          onPress={editMaxParticipants}
          style={{
            backgroundColor: theme.colors['surface.default'],
            borderRadius: theme.borderRadii['radius.lg'],
            marginTop: theme.spacing['spacing.general.x4'],
            padding: theme.spacing['spacing.general.x4'],
          }}
        >
          <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between">
            <Column gap="spacing.general.x1" style={{ maxWidth: '75%' }}>
              <Text fontWeight="700" variant="bodyBase">
                {t('subscriptionsSection.text', { ns: 'one-listing' })}
              </Text>
              {numParticipants !== undefined && numParticipants !== null ? (
                <Row style={{ flexShrink: 1 }}>
                  <Text
                    color="text.primary"
                    style={{
                      backgroundColor: theme.colors['surface.brand.secondary-1.default'],
                      paddingHorizontal: theme.spacing['spacing.general.x2'],
                      paddingVertical: 4,
                      borderRadius: theme.borderRadii['radius.sm'],
                    }}
                  >
                    {t('subscriptionsSection.participants', {
                      ns: 'one-listing',
                      count: numParticipants,
                    })}
                  </Text>
                </Row>
              ) : (
                <Text color="text.secondary">({t('optional', { ns: 'common' })})</Text>
              )}
            </Column>
            <ControlledInput
              {...{ control }}
              autoWidth
              innerRef={maxParticipantsInputRef}
              keyboardType="number-pad"
              name="maxParticipants"
              textAlign="center"
            />
          </Row>
        </Pressable>
      </View>
    </>
  );
}
